import { splitBadgeId, badgeIdFor, getLangInfo } from '@/data/languages';

// Badge catalog - describes all possible badges with icons, colors, and criteria descriptions

export const BADGE_CATALOG = {
  'first-steps': {
    id: 'first-steps',
    name: 'First Steps',
    description: 'Complete your first quiz',
    icon: '🌱',
    color: '#22C55E',
    tier: 'Bronze',
    accent: '#4ADE80'
  },
  'java-novice': {
    id: 'java-novice',
    name: 'Java Novice',
    description: 'Complete 3 Java Basics quizzes',
    icon: '☕',
    color: '#F97316',
    tier: 'Bronze',
    accent: '#FB923C'
  },
  'java-master': {
    id: 'java-master',
    name: 'Java Master',
    description: 'Complete all 10 Java Basics quizzes',
    icon: '👑',
    color: '#FFB800',
    tier: 'Gold',
    accent: '#FCD34D'
  },
  'perfect-score': {
    id: 'perfect-score',
    name: 'Perfect Score',
    description: 'Score 100% on any quiz',
    icon: '💯',
    color: '#EC4899',
    tier: 'Silver',
    accent: '#F472B6'
  },
  'array-master': {
    id: 'array-master',
    name: 'Array Master',
    description: 'Complete all Array Traversal problems',
    icon: '📊',
    color: '#007AFF',
    tier: 'Silver',
    accent: '#00D4FF'
  },
  'two-pointer-pro': {
    id: 'two-pointer-pro',
    name: 'Two Pointer Pro',
    description: 'Complete all Two Pointer problems',
    icon: '👉',
    color: '#8B5CF6',
    tier: 'Silver',
    accent: '#A78BFA'
  },
  'search-ninja': {
    id: 'search-ninja',
    name: 'Search Ninja',
    description: 'Complete all Searching problems',
    icon: '🔍',
    color: '#06B6D4',
    tier: 'Silver',
    accent: '#67E8F9'
  },
  'sort-virtuoso': {
    id: 'sort-virtuoso',
    name: 'Sort Virtuoso',
    description: 'Complete all Sorting problems',
    icon: '📈',
    color: '#10B981',
    tier: 'Silver',
    accent: '#34D399'
  },
  'recursion-wizard': {
    id: 'recursion-wizard',
    name: 'Recursion Wizard',
    description: 'Complete all Recursion problems',
    icon: '🧙',
    color: '#A855F7',
    tier: 'Gold',
    accent: '#C084FC'
  },
  'backtrack-master': {
    id: 'backtrack-master',
    name: 'Backtrack Master',
    description: 'Complete all Backtracking problems',
    icon: '↩️',
    color: '#F43F5E',
    tier: 'Gold',
    accent: '#FB7185'
  },
  'dp-expert': {
    id: 'dp-expert',
    name: 'DP Expert',
    description: 'Complete all Dynamic Programming problems',
    icon: '🧩',
    color: '#EAB308',
    tier: 'Gold',
    accent: '#FDE047'
  },
  'graph-guru': {
    id: 'graph-guru',
    name: 'Graph Guru',
    description: 'Complete all Graph problems',
    icon: '🌐',
    color: '#0EA5E9',
    tier: 'Gold',
    accent: '#38BDF8'
  },
  'tree-whisperer': {
    id: 'tree-whisperer',
    name: 'Tree Whisperer',
    description: 'Complete all Tree problems',
    icon: '🌳',
    color: '#16A34A',
    tier: 'Gold',
    accent: '#4ADE80'
  },
  'string-slayer': {
    id: 'string-slayer',
    name: 'String Slayer',
    description: 'Complete all String Algorithm problems',
    icon: '⚔️',
    color: '#DC2626',
    tier: 'Gold',
    accent: '#F87171'
  },
  'dsa-champion': {
    id: 'dsa-champion',
    name: 'DSA Champion',
    description: 'Complete all 30 DSA problems',
    icon: '🏆',
    color: '#FBBF24',
    tier: 'Platinum',
    accent: '#FCD34D'
  },
  'full-stack-learner': {
    id: 'full-stack-learner',
    name: 'Full Stack Learner',
    description: 'Master all quizzes AND all DSA problems',
    icon: '🎖️',
    color: '#F59E0B',
    tier: 'Platinum',
    accent: '#FCD34D'
  },
};

const GENERAL_BADGES = ['first-steps', 'perfect-score'];

const FALLBACK = { name: '', description: '', icon: '🏅', color: '#71717A', tier: 'Bronze', accent: '#A1A1AA' };

// Course badges are per language: `${base}` for Java, `${base}--${lang}` otherwise
export const getBadgeInfo = (badgeId) => {
  const { base, lang } = splitBadgeId(badgeId);
  const info = BADGE_CATALOG[base];
  if (!info) return { ...FALLBACK, id: badgeId, name: badgeId };
  if (GENERAL_BADGES.includes(base)) return { ...info, id: badgeId };
  const L = getLangInfo(lang).label;
  if (base === 'java-novice') return { ...info, id: badgeId, name: `${L} Novice`, description: `Complete 3 ${L} Basics quizzes`, language: lang };
  if (base === 'java-master') return { ...info, id: badgeId, name: `${L} Master`, description: `Complete all 10 ${L} Basics quizzes`, language: lang };
  return { ...info, id: badgeId, name: `${info.name} · ${L}`, description: `${info.description} in the ${L} course`, language: lang };
};

export const getBadgesForLanguage = (lang) => Object.keys(BADGE_CATALOG).map(base =>
  getBadgeInfo(GENERAL_BADGES.includes(base) ? base : badgeIdFor(base, lang)));
